if (!customElements.get('hero-slideshow-component')) {
  class HeroSlideshowComponent extends SlideshowComponent {
    constructor() {
      super();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      // Re-trigger setAutoPlay now that reducedMotion is initialized
      if (this.slider.getAttribute('data-autoplay') === 'true') {
        this.setAutoPlay();
      }

      this.initSideButtons();
      this.initMouseDrag();
    }

    initSideButtons() {
      const prevButtons = this.querySelectorAll('button[name="previous"]');
      const nextButtons = this.querySelectorAll('button[name="next"]');

      prevButtons.forEach((btn) => {
        btn.addEventListener('click', this.onButtonClick.bind(this));
      });

      nextButtons.forEach((btn) => {
        btn.addEventListener('click', this.onButtonClick.bind(this));
      });
    }

    initMouseDrag() {
      if (!this.slider) return;

      this.isMouseDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.isDragging = false;
      this.preventClick = false;

      const onMouseDown = (e) => {
        // Ignore clicks on control buttons/arrows
        if (e.target.closest('button, .ctt-hero-slider__controls')) return;
        if (e.button !== 0) return; // Only left mouse button

        this.isMouseDown = true;
        this.isDragging = false;
        this.startX = e.clientX;
        this.scrollLeftStart = this.slider.scrollLeft;
      };

      const onMouseMove = (e) => {
        if (!this.isMouseDown) return;

        const deltaX = e.clientX - this.startX;
        if (!this.isDragging && Math.abs(deltaX) > 5) {
          this.isDragging = true;
          this.preventClick = true;
          this.slider.classList.add('is-dragging');
          this.pause();
        }

        if (this.isDragging) {
          e.preventDefault();
          this.slider.scrollLeft = this.scrollLeftStart - deltaX;
        }
      };

      const onMouseUpOrLeave = (e) => {
        if (!this.isMouseDown) return;
        this.isMouseDown = false;

        if (this.isDragging) {
          this.slider.classList.remove('is-dragging');
          const deltaX = e.clientX - this.startX;
          const threshold = Math.min(80, (this.sliderItemOffset || this.slider.clientWidth) * 0.12);

          if (deltaX < -threshold) {
            // Dragged left -> Next slide
            this.onButtonClick({
              preventDefault: () => {},
              currentTarget: { name: 'next' }
            });
          } else if (deltaX > threshold) {
            // Dragged right -> Previous slide
            this.onButtonClick({
              preventDefault: () => {},
              currentTarget: { name: 'previous' }
            });
          } else {
            // Snap back to current slide position
            const targetPos = (this.currentPage - 1) * (this.sliderItemOffset || this.slider.clientWidth);
            this.setSlidePosition(targetPos);
          }

          setTimeout(() => {
            this.preventClick = false;
            this.isDragging = false;
          }, 100);
        }

        if (this.slider.getAttribute('data-autoplay') === 'true') {
          this.play();
        }
      };

      // Prevent accidental link/button click on drag release
      this.addEventListener(
        'click',
        (e) => {
          if (this.preventClick) {
            e.preventDefault();
            e.stopPropagation();
            this.preventClick = false;
          }
        },
        true
      );

      this.slider.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUpOrLeave);
    }

    initPages() {
      super.initPages();
      // Ensure totalPages equals the exact number of slides in full-width hero slider
      const totalSlides = this.sliderItemsToShow ? this.sliderItemsToShow.length : this.sliderItems.length;
      if (totalSlides > 0) {
        this.totalPages = totalSlides;
        if (this.pageTotalElement) {
          this.pageTotalElement.textContent = this.totalPages;
        }
      }
    }

    setAutoPlay() {
      this.autoplaySpeed = this.slider.dataset.speed * 1000;
      this.addEventListener('mouseover', this.focusInHandling.bind(this));
      this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
      this.addEventListener('focusin', this.focusInHandling.bind(this));
      this.addEventListener('focusout', this.focusOutHandling.bind(this));

      if (this.querySelector('.slideshow__autoplay')) {
        this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
        this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
        this.autoplayButtonIsSetToPlay = true;
        this.play();
      } else {
        (this.reducedMotion && this.reducedMotion.matches) || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
      }
    }

    focusOutHandling(event) {
      if (this.isMouseDown || this.isDragging) return;
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
      } else if ((!this.reducedMotion || !this.reducedMotion.matches) && !this.announcementBarArrowButtonWasClicked) {
        this.play();
      }
    }

    focusInHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
          this.play();
        } else if (this.autoplayButtonIsSetToPlay) {
          this.pause();
        }
      } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
        this.pause();
      } else {
        this.pause();
      }
    }

    update() {
      super.update();
      if (!this.slider) return;

      const totalSlides = this.sliderItemsToShow ? this.sliderItemsToShow.length : this.sliderItems.length;
      if (totalSlides > 0) {
        this.totalPages = totalSlides;
        const itemOffset = this.sliderItemOffset || this.slider.clientWidth;
        const rawPage = Math.round(this.slider.scrollLeft / itemOffset) + 1;
        this.currentPage = Math.min(Math.max(1, rawPage), totalSlides);

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.currentPage;
          this.pageTotalElement.textContent = this.totalPages;
        }
      }

      if (this.prevButton) this.prevButton.removeAttribute('disabled');

      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
      if (!this.sliderControlButtons.length) return;

      this.sliderControlButtons.forEach((link) => {
        link.classList.remove('slider-counter__link--active');
        link.classList.remove('ctt-hero-slider__dot--active');
        link.removeAttribute('aria-current');
      });

      const activeIndex = this.currentPage - 1;
      if (this.sliderControlButtons[activeIndex]) {
        this.sliderControlButtons[activeIndex].classList.add('slider-counter__link--active');
        this.sliderControlButtons[activeIndex].classList.add('ctt-hero-slider__dot--active');
        this.sliderControlButtons[activeIndex].setAttribute('aria-current', 'true');
      }
    }

    autoRotateSlides() {
      if (this.isMouseDown || this.isDragging) return;
      if (!this.sliderItemOffset) this.initPages();
      const totalSlides = this.sliderItemsToShow ? this.sliderItemsToShow.length : this.sliderItems.length;
      const slideScrollPosition =
        this.currentPage >= totalSlides ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

      this.setSlidePosition(slideScrollPosition);
      this.applyAnimationToAnnouncementBar();
    }
  }

  customElements.define('hero-slideshow-component', HeroSlideshowComponent);
}